import type {CellValue, RowType} from '../data/reportRowModel';

/** The words an unset task cell shows in place of `N/A`, keyed by the cell path the value would
 * have been at. report-service omits these nodes when the value is null, so the cell arrives
 * undefined: "nobody has set this", not "this does not apply". The grouping header reads the same
 * ids, so the cell and the `No deliverable (5)` bucket above it cannot drift apart.
 *
 * A `Map` rather than an object literal because the key is a path off the report definition, and a
 * plain object would answer `constructor`/`toString` off its prototype. */
export const EMPTY_VALUE_MESSAGE_ID_BY_PATH: ReadonlyMap<string, string> = new Map([
	['task.sprint.name', 'ai_report_builder.empty.no_sprint'],
	['task.deliverable.name', 'ai_report_builder.empty.no_deliverable'],
	['task.assignees.firstName', 'ai_report_builder.empty.no_assignee'],
	['task.dates.deadline', 'ai_report_builder.empty.no_deadline'],
]);

/** Only a task or subtask row can be "unset": a deliverable wrapper carries id/name alone, so every
 * other cell on it is undefined by construction and must keep rendering as it always has. */
export function isUnsettableRow(rowType: RowType | undefined): boolean {
	return rowType === 'task' || rowType === 'subtask';
}

/** The one column where a zero means "not estimated". */
export const ESTIMATE_PATH = 'task.work.estimate';

/** The task table's estimate columns are stored NOT NULL DEFAULT 0, so "never estimated" and
 * "estimated at zero" are one stored value. Zero is read as unestimated: the reader loses the rare
 * deliberate zero, and in exchange an empty estimate stops posing as a number. Only the estimate - a
 * remaining of zero means the work is done. */
export function isUnestimated(path: string | undefined, cell: CellValue | undefined): boolean {
	return path === ESTIMATE_PATH && cell?.kind === 'duration' && cell.minutes === 0;
}
